import React, { useEffect, useMemo, useState } from "react";
import { ButtonBase, Modal, Stack, Typography, useTheme } from "@mui/material";
import Brand from "../../api/Brand";
import ColoredIcon from "../ColoredIcon";
import NotificationIcon from "../../img/Notify_Passive.svg";
import NotificationActiveIcon from "../../img/Notify_Active.svg";
import MenuIcon from "../../img/Sandvich.svg";
import Menu from "./Menu";
import Notification from "../../api/Notification";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { APP_MAX_WIDTH } from "../../constants/layout";
import BrandAvatar from "../BrandAvatar";
import { HEADER } from "../../constants/testIds";

interface HeaderProps {
    isPartner?: boolean;
    brand?: Brand;
    logo?: string | null;
    scrollY?: number; // When provided, header transitions from transparent to opaque
}

const Header = ({ isPartner = false, brand: brandProp, logo: logoProp, scrollY }: HeaderProps) => {
    const navigate = useNavigate();

    const theme = useTheme();

    const [cookie] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookie;

    const [brandLocal, setBrandLocal] = useState<Brand | undefined>();
    const [logoLocal, setLogoLocal] = useState<string | null | undefined>();

    const brand = brandProp ?? brandLocal;
    const logo = logoProp !== undefined ? logoProp : logoLocal;

    const [showMenu, setShowMenu] = useState(false);
    const [newNotifications, setNewNotifications] = useState(false);

    const [loading, setLoading] = useState(!brandProp);

    const onNotificationsClick = () => navigate("/notifications");
    const onLogoClick = () => navigate(isPartner ? "/partner" : "/");
    const onMenuClick = () => setShowMenu(true);
    const onMenuClose = () => setShowMenu(false);

    useEffect(() => {
        if (brandProp) return; // Skip fetch when brand is provided via props
        if (!uuid || !brand_id) {
            navigate("/");
            return;
        }
        setLoading(true);
        Brand.info(uuid, brand_id)
            .then((res) => {
                setBrandLocal(res);
                setLogoLocal(res?.logo);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [uuid, brand_id, navigate, brandProp]);

    useEffect(() => {
        if (!uuid || !brand_id) return;
        Notification.countUnread(uuid, brand_id)
            .then((res) => setNewNotifications((res ?? 0) > 0))
            .catch(console.error);
    }, [uuid, brand_id]);

    // Scroll-aware transparency: when scrollY is provided, transition from transparent to opaque
    const isTransparentMode = scrollY !== undefined;
    const opacity = useMemo(() => {
        if (!isTransparentMode) return 1;
        return Math.min(1, scrollY / 100);
    }, [isTransparentMode, scrollY]);

    const isOverHero = isTransparentMode && opacity < 0.5;
    const iconColor = isOverHero ? "white" : undefined;

    return (
        <Stack
            className="header"
            direction="row"
            width="100%"
            maxWidth={APP_MAX_WIDTH} // Match AppWrapper
            marginX="auto"
            position="fixed"
            top={0}
            left={0}
            right={0}
            zIndex={10}
            height="56px"
            minHeight="56px"
            maxHeight="56px"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{
                borderBottom: isTransparentMode && opacity < 1
                    ? `1px solid rgba(0,0,0,${0.06 * opacity})`
                    : "1px solid rgba(0,0,0,0.06)",
                bgcolor: isTransparentMode
                    ? `rgba(255,255,255,${opacity})`
                    : "white",
                transition: "background-color 0.2s, border-color 0.2s",
                pointerEvents: "auto",
            }}
            data-testid={HEADER.CONTAINER}
        >
            <Stack
                direction="row"
                height="56px"
                minHeight="56px"
                maxHeight="56px"
                alignItems="center"
                spacing={1.5}
                paddingLeft={2}
            >
                <ButtonBase
                    onClick={onLogoClick}
                    sx={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "36px",
                        backgroundColor: theme.palette.primary.main,
                    }}
                    data-testid={HEADER.LOGO_BUTTON}
                >
                    <BrandAvatar logo={logo} size={36} loading={loading} />
                </ButtonBase>
                <Typography
                    className="no-translation"
                    fontWeight={600}
                    fontSize="17px"
                    lineHeight="21.28px"
                    sx={{
                        color: isOverHero ? "white" : "text.primary",
                        transition: "color 0.2s",
                    }}
                    data-testid={HEADER.BRAND_NAME}
                >
                    {brand?.name}
                </Typography>
            </Stack>
            <Stack
                direction="row"
                height="56px"
                minHeight="56px"
                maxHeight="56px"
                alignItems="center"
                spacing={1}
                paddingRight={2}
                sx={{ pointerEvents: "auto" }}
            >
                {!isPartner && (
                    <ButtonBase
                        onClick={onNotificationsClick}
                        sx={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                        data-testid={HEADER.NOTIFICATIONS_BUTTON}
                    >
                        <ColoredIcon
                            size="24px"
                            icon={
                                newNotifications
                                    ? NotificationActiveIcon
                                    : NotificationIcon
                            }
                            color={iconColor}
                        />
                    </ButtonBase>
                )}
                <ButtonBase
                    onClick={onMenuClick}
                    sx={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    data-testid={HEADER.MENU_BUTTON}
                >
                    <ColoredIcon size="24px" icon={MenuIcon} color={iconColor} />
                </ButtonBase>
            </Stack>
            <Modal open={showMenu} onClose={onMenuClose}>
                <Menu onClose={onMenuClose} isPartner={isPartner} />
            </Modal>
        </Stack>
    );
};

export default Header;
